import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  MapPin, 
  FileText, 
  Calendar, 
  Users, 
  UserMinus,
  ArrowRight,
  RefreshCw,
  Plus,
  Clock,
  CheckCircle,
  AlertCircle,
  Ticket,
  MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE_URL } from '@/lib/apiBase';

const API = API_BASE_URL;

const DashboardPage = () => {
  const { user, isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [syncRequestStats, setSyncRequestStats] = useState(null);
  const [offboardingStats, setOffboardingStats] = useState(null);
  const [ticketStats, setTicketStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchSyncRequestStats = async () => {
    try {
      const response = await axios.get(`${API}/sync-requests/stats/summary`);
      setSyncRequestStats(response.data);
    } catch (error) {
      console.error('Failed to fetch sync request stats:', error);
    }
  };

  const fetchOffboardingStats = async () => {
    try {
      const response = await axios.get(`${API}/offboarding/stats/summary`);
      setOffboardingStats(response.data);
    } catch (error) {
      console.error('Failed to fetch offboarding stats:', error);
    }
  };

  const fetchTicketStats = async () => {
    try {
      const response = await axios.get(`${API}/tickets/stats/summary`);
      setTicketStats(response.data);
    } catch (error) {
      console.error('Failed to fetch ticket stats:', error);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchSyncRequestStats();
    fetchOffboardingStats();
    fetchTicketStats();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchStats();
    fetchSyncRequestStats();
    fetchOffboardingStats();
    fetchTicketStats();
  };

  const StatCard = ({ icon: Icon, title, value, color, link }) => (
    <Card className="card-hover" data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500">{title}</p>
            <p className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {loading ? '—' : value}
            </p>
          </div>
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${color}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
        {link && (
          <Link 
            to={link} 
            className="mt-4 flex items-center text-sm text-indigo-600 hover:text-indigo-700 font-medium"
          >
            View all <ArrowRight className="w-4 h-4 ml-1" />
          </Link>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Dashboard
          </h1>
          <p className="text-slate-500 mt-1">
            Welcome back, {user?.name}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {isAdmin() && (
            <Button asChild className="bg-indigo-600 hover:bg-indigo-700" size="sm">
              <Link to="/locations" data-testid="add-location-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Location
              </Link>
            </Button>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="dashboard-grid">
        <StatCard 
          icon={MapPin}
          title="Locations"
          value={stats?.total_locations || 0}
          color="bg-indigo-100 text-indigo-600"
          link="/locations"
        />
        <StatCard 
          icon={FileText}
          title="Custom Fields"
          value={stats?.total_fields || 0}
          color="bg-emerald-100 text-emerald-600"
        />
        <StatCard 
          icon={Calendar}
          title="Calendars"
          value={stats?.total_calendars || 0}
          color="bg-amber-100 text-amber-600"
        />
        {isAdmin() && (
          <>
            <StatCard 
              icon={Users}
              title="Users"
              value={stats?.total_users || 0}
              color="bg-blue-100 text-blue-600"
              link="/users"
            />
          </>
        )}
      </div>

      {/* Sync & Offboarding Request Cards - Visible to all users */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Sync Requests Summary */}
        <Card data-testid="sync-requests-summary">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <RefreshCw className="w-5 h-5 text-indigo-600" />
              Sync Requests
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {syncRequestStats ? (
              <>
                {/* Pending */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-amber-600" />
                    <span className="font-medium text-amber-900">Pending</span>
                  </div>
                  <span className="text-xl font-bold text-amber-700">{syncRequestStats.pending}</span>
                </div>
                
                {/* In Progress */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-blue-50 border border-blue-200">
                  <div className="flex items-center gap-3">
                    <RefreshCw className="w-5 h-5 text-blue-600" />
                    <span className="font-medium text-blue-900">In Progress</span>
                  </div>
                  <span className="text-xl font-bold text-blue-700">{syncRequestStats.in_progress}</span>
                </div>
                
                {/* Completed */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                    <span className="font-medium text-emerald-900">Completed</span>
                  </div>
                  <span className="text-xl font-bold text-emerald-700">{syncRequestStats.completed}</span>
                </div>
                
                {/* View All Link - admin only sees the full list */}
                {isAdmin() && (
                  <Link 
                    to="/sync-requests" 
                    className="flex items-center justify-center gap-2 mt-2 p-2 rounded-lg border border-indigo-200 text-indigo-600 hover:bg-indigo-50 transition-colors font-medium"
                    data-testid="view-sync-requests"
                  >
                    View All Requests
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </>
            ) : (
              <div className="text-center py-4 text-slate-500">
                <RefreshCw className="w-8 h-8 mx-auto mb-2 text-slate-300 animate-spin" />
                <p>Loading...</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Offboarding Summary */}
        <Card data-testid="offboarding-summary">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <UserMinus className="w-5 h-5 text-rose-600" />
              Offboarding
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {offboardingStats ? (
              <>
                {/* Scheduled */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-amber-600" />
                    <span className="font-medium text-amber-900">Scheduled</span>
                  </div>
                  <span className="text-xl font-bold text-amber-700">{offboardingStats.scheduled}</span>
                </div>
                
                {/* In Progress */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-blue-50 border border-blue-200">
                  <div className="flex items-center gap-3">
                    <RefreshCw className="w-5 h-5 text-blue-600" />
                    <span className="font-medium text-blue-900">In Progress</span>
                  </div>
                  <span className="text-xl font-bold text-blue-700">{offboardingStats.in_progress}</span>
                </div>
                
                {/* Completed */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                    <span className="font-medium text-emerald-900">Completed</span>
                  </div>
                  <span className="text-xl font-bold text-emerald-700">{offboardingStats.completed}</span>
                </div>
                
                {/* View All Link */}
                <Link 
                  to="/offboarding" 
                  className="flex items-center justify-center gap-2 mt-2 p-2 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 transition-colors font-medium"
                  data-testid="view-offboarding"
                >
                  View All Tasks
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </>
            ) : (
              <div className="text-center py-4 text-slate-500">
                <RefreshCw className="w-8 h-8 mx-auto mb-2 text-slate-300 animate-spin" />
                <p>Loading...</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Support Tickets Summary */}
        <Card data-testid="tickets-summary">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <MessageSquare className="w-5 h-5 text-purple-600" />
              Support Tickets
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {ticketStats ? (
              <>
                {/* Open */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                    <span className="font-medium text-amber-900">Open</span>
                  </div>
                  <span className="text-xl font-bold text-amber-700">{ticketStats.open}</span>
                </div>
                
                {/* In Progress */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-blue-50 border border-blue-200">
                  <div className="flex items-center gap-3">
                    <RefreshCw className="w-5 h-5 text-blue-600" />
                    <span className="font-medium text-blue-900">In Progress</span>
                  </div>
                  <span className="text-xl font-bold text-blue-700">{ticketStats.in_progress}</span>
                </div>
                
                {/* Resolved */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                    <span className="font-medium text-emerald-900">Resolved</span>
                  </div>
                  <span className="text-xl font-bold text-emerald-700">{ticketStats.resolved}</span>
                </div>
                
                {/* View All Link */}
                <Link 
                  to="/tickets" 
                  className="flex items-center justify-center gap-2 mt-2 p-2 rounded-lg border border-purple-200 text-purple-600 hover:bg-purple-50 transition-colors font-medium"
                  data-testid="view-tickets"
                >
                  View All Tickets
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </>
            ) : (
              <div className="text-center py-4 text-slate-500">
                <RefreshCw className="w-8 h-8 mx-auto mb-2 text-slate-300 animate-spin" />
                <p>Loading...</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions - Admin Only */}
        {isAdmin() && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Link 
                to="/locations" 
                className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
                data-testid="quick-action-locations"
              >
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-indigo-600" />
                  <span className="font-medium text-slate-700">Manage Locations</span>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </Link>
              <Link 
                to="/users" 
                className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
                data-testid="quick-action-users"
              >
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-slate-700">Manage Users</span>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </Link>
              <Link 
                to="/settings" 
                className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
                data-testid="quick-action-settings"
              >
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                  <span className="font-medium text-slate-700">Settings</span>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </Link>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Getting Started (for non-admin or empty state) */}
      {stats?.total_locations === 0 && (
        <Card className="border-dashed border-2 border-slate-200">
          <CardContent className="py-12 text-center">
            <MapPin className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Get Started
            </h3>
            <p className="text-slate-500 mb-6 max-w-md mx-auto">
              Connect your first CRM location to start syncing custom fields and calendars.
            </p>
            {isAdmin() && (
              <Button asChild className="bg-indigo-600 hover:bg-indigo-700">
                <Link to="/locations" data-testid="get-started-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Location
                </Link>
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardPage;
